import { Auth0Provider } from '@auth0/auth0-react';
import type { AppConfig } from '@/config/AppConfig';
import { useConfig } from '@/config/ConfigProvider';
import { isOidcCallbackPath } from './callback-path';
import { AUTH0_PROVIDER_KEY } from './provider-config';
import {
  EAuthProviderType,
  type IAuthAdapter,
  type IAuthAdapterProviderProps,
} from './types';

const onRedirectCallback = (redirectState?: { returnTo?: string }) => {
  if (redirectState?.returnTo) {
    const nextUrl = new URL(redirectState.returnTo, window.location.origin);
    // Only redirect if it's the same origin
    if (nextUrl.origin === window.location.origin) {
      window.location.replace(nextUrl.toString());
    }
  }
};

// Check if the current hostname is localhost
const isLocalHost = window.location.hostname === 'localhost';

/**
 * Builds the `Auth0Provider` props from the app config.
 *
 * @param {AppConfig} appConfig - Resolved runtime configuration
 * @returns {object} Props for `Auth0Provider`
 */
export const createAuth0Config = (appConfig: AppConfig) => {
  return {
    // Only mounted when Auth0 is configured; `validateAuth0Config` reports a gap.
    domain: appConfig.auth0Domain ?? '',
    clientId: appConfig.auth0ClientId ?? '',
    authorizationParams: {
      redirect_uri: `${window.location.origin}/login`,
      audience: appConfig.auth0Audience,
      // Required for refresh tokens; without this, getAccessTokenSilently can throw "Missing Refresh Token"
      scope: 'openid profile email offline_access',
    },
    onRedirectCallback,
    // Auth0's SDK claims any URL carrying `code`/`error`. On a federated
    // callback that response is not its to consume. False on every path Auth0
    // itself redirects to, so its own flow is unchanged.
    skipRedirectCallback: isOidcCallbackPath(window.location.pathname),
    cacheLocation:
      import.meta.env.VITE_E2E_MODE || isLocalHost
        ? ('localstorage' as const)
        : ('memory' as const),
    useRefreshTokens: true,
    useRefreshTokensFallback: true,
    sessionCheckExpiryDays: 7,
  };
};

/**
 * Validate required Auth0 configuration.
 *
 * @param {AppConfig} config - Resolved runtime configuration
 * @returns {boolean} true when domain, client id and audience are all present
 */
export const validateAuth0Config = (config: AppConfig): boolean => {
  const hasRequiredFields =
    config.auth0Domain && config.auth0ClientId && config.auth0Audience;

  if (!hasRequiredFields) {
    console.warn('Missing required Auth0 configuration in AppConfig:', {
      hasDomain: !!config.auth0Domain,
      hasClientId: !!config.auth0ClientId,
      hasAudience: !!config.auth0Audience,
    });
    return false;
  }

  return true;
};

/**
 * Mounts the Auth0 SDK context that backs `useAuth`.
 *
 * @param {IAuthAdapterProviderProps} props - Component props
 * @param {ReactNode} props.children - Subtree that consumes the session
 * @returns {JSX.Element} The Auth0 provider
 */
const Auth0AdapterProvider = ({ children }: IAuthAdapterProviderProps) => {
  const config = useConfig();

  return (
    <Auth0Provider {...createAuth0Config(config)}>{children}</Auth0Provider>
  );
};

/**
 * Builds the Auth0 adapter.
 *
 * @param {AppConfig} appConfig - Resolved runtime configuration
 * @returns {IAuthAdapter} Adapter backed by the Auth0 SDK
 */
export const createAuth0Adapter = (appConfig: AppConfig): IAuthAdapter => ({
  key: AUTH0_PROVIDER_KEY,
  type: EAuthProviderType.AUTH0,
  Provider: Auth0AdapterProvider,
  validateConfig: () => validateAuth0Config(appConfig),
});
